import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  ApplicationConfig,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { provideServiceWorker, SwUpdate } from '@angular/service-worker';
import {
  LANGUAGE_INTERCEPTOR_API_BASE_URL,
  LANGUAGE_INTERCEPTOR_CONFIG,
  LanguageInterceptor,
  LanguageService,
} from '@catch/services';
import { provideTransloco } from '@jsverse/transloco';
import { provideTranslocoMessageformat } from '@jsverse/transloco-messageformat';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import Aura from '@primeuix/themes/aura';
import { CookieService } from 'ngx-cookie-service';
import { provideEchartsCore } from 'ngx-echarts';
import { provideLottieOptions } from 'ngx-lottie';
import { provideUiTour } from 'ngx-ui-tour-md-menu';
import { providePrimeNG } from 'primeng/config';
import { MARKDOWN_EDITOR_KIRA_PORT } from '@catch/markdown-editor';
import { environment } from '../environments/environment';
import { MarkdownEditorKiraAdapter } from './adapters/markdown-editor-kira.adapter';
import { fullReducers, metaReducers } from './app.reducer';
import { routes } from './app.routes';
import { AuthenticationInterceptor } from './interceptors/authentication.interceptor';
import { DedupeGetInterceptor } from './interceptors/dedupe-get.interceptor';
import { ErrorInterceptor } from './interceptors/error.interceptor';
import { OfflineInterceptor } from './interceptors/offline.interceptor';
import { OnboardingService } from './services/onboarding.service';
import { TourService } from './services/tour.service';
import { AccountCompletionEffects } from './store/account-completion/account-completion.effects';
import { DashboardKpisEffects } from './store/dashboard-kpis/dashboard-kpis.effects';
import { PerformanceAnalysisEffects } from './store/performance-analysis/performance-analysis.effects';
import { ApplicationsEffects } from './store/applications/applications.effects';
import { AuthEffects } from './store/auth/auth.effects';
import { CompanyAccountEffects } from './store/company-account/company-account.effects';
import { CompanyIconEffects } from './store/company-icon/company-icon.effects';
import { CompanyLogoEffects } from './store/company-logo/company-logo.effects';
import { CompanyRedFlagsEffects } from './store/company-red-flags/company-red-flags.effects';
import { ConfigEffects } from './store/config/config.effects';
import { ConnectionEffects } from './store/connection/connection.effects';
import { CreditsEffects } from './store/credits/credits.effects';
import { DeclineReasonsEffects } from './store/decline-reasons/decline-reasons.effects';
import { DesignEffects } from './store/design/design.effects';
import { EmailEffects } from './store/emails/emails.effects';
import { FacilityEffects } from './store/facilities/facilities.effects';
import { JobsEffects } from './store/jobs/jobs.effects';
import { NotificationSettingsEffects } from './store/notification-settings/notification-settings.effects';
import { NotificationsEffects } from './store/notifications/notifications.effects';
import { PaymentEffects } from './store/payment/payment.effects';
import { SignatureEffects } from './store/signatures/signatures.effects';
import { SubscriptionEffects } from './store/subscription/subscription.effects';
import { TfaMethodEffects } from './store/tfa-methods/tfa-methods.effects';
import { TodosEffects } from './store/todos/todos.effects';
import { UserAccountEffects } from './store/user-account/user-account.effects';
import { UserAvatarEffects } from './store/user-avatar/user-avatar.effects';
import { UsersEffects } from './store/users/users.effects';
import { ExportDownloadsEffects } from './store/export-downloads/export-downloads.effects';
import { TranslocoAppLoader } from './transloco-loader';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimations(),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    provideTransloco({
      config: {
        availableLangs: ['en-US', 'de-DE'],
        defaultLang: 'de-DE',
        reRenderOnLangChange: true,
        prodMode: environment.production,
        missingHandler: {
          logMissingKey: false,
        },
        fallbackLang: 'en-US',
      },
      loader: TranslocoAppLoader,
    }),
    providePrimeNG({
      theme: {
        preset: Aura,
      },
      pt: {
        paginator: {
          pcRowPerPageDropdown: {
            pcOverlay: {
              root: {
                class: 'p-paginator-rpp-dropdown',
              },
            },
          },
        },
      },
    }),
    provideEchartsCore({ echarts: () => import('echarts') }),
    provideLottieOptions({
      player: () => import('lottie-web'),
    }),
    provideStore(fullReducers, { metaReducers }),
    provideStoreDevtools({
      maxAge: 200,
      logOnly: environment.production,
    }),
    provideRouterStore(),
    provideEffects(
      AccountCompletionEffects,
      DashboardKpisEffects,
      PerformanceAnalysisEffects,
      ApplicationsEffects,
      AuthEffects,
      CompanyAccountEffects,
      CompanyIconEffects,
      CompanyLogoEffects,
      CompanyRedFlagsEffects,
      ConfigEffects,
      ConnectionEffects,
      CreditsEffects,
      DeclineReasonsEffects,
      DesignEffects,
      EmailEffects,
      FacilityEffects,
      JobsEffects,
      NotificationSettingsEffects,
      NotificationsEffects,
      PaymentEffects,
      SignatureEffects,
      SubscriptionEffects,
      TfaMethodEffects,
      TodosEffects,
      UserAccountEffects,
      UserAvatarEffects,
      UsersEffects,
      ExportDownloadsEffects,
    ),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerImmediately',
    }),
    SwUpdate,
    provideAppInitializer(() => {
      const swUpdate = inject(SwUpdate);
      if (swUpdate.isEnabled) {
        const currentUrl = window.location.pathname;
        const isLoginRoute = currentUrl.startsWith('/auth/login');
        const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        const isFreshLoad =
          !sessionStorage.getItem('app-initialized') || navEntry?.type === 'reload';

        if (isLoginRoute || isFreshLoad) {
          sessionStorage.setItem('app-initialized', 'true');
          return swUpdate.checkForUpdate().then((updateAvailable) => {
            if (updateAvailable) {
              return swUpdate.activateUpdate().then(() => {
                window.location.reload();
                return Promise.resolve();
              });
            }
            return Promise.resolve();
          });
        }
      }
      return Promise.resolve();
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OfflineInterceptor,
      multi: true,
    },
    AuthenticationInterceptor,
    {
      provide: HTTP_INTERCEPTORS,
      useExisting: AuthenticationInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: LANGUAGE_INTERCEPTOR_API_BASE_URL,
      useValue: environment.apiBaseUrl,
    },
    {
      provide: LANGUAGE_INTERCEPTOR_CONFIG,
      useValue: { allowFrFromBrowser: false },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DedupeGetInterceptor,
      multi: true,
    },
    {
      provide: LOCALE_ID,
      deps: [LanguageService],
      useFactory: (languageService: LanguageService) => languageService.currentLanguage.value,
    },
    provideTranslocoMessageformat(),
    provideUiTour(),
    CookieService,
    OnboardingService,
    TourService,
    MarkdownEditorKiraAdapter,
    {
      provide: MARKDOWN_EDITOR_KIRA_PORT,
      useExisting: MarkdownEditorKiraAdapter,
    },
  ],
};
